import "@fontsource-variable/figtree";
import "@fontsource/jost/200.css";
import "@fontsource/jost/300.css";
import "@fontsource/jost/400.css";
import "./styles.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useRouterState,
} from "@tanstack/react-router";
import { lazy, StrictMode, Suspense, useEffect, type ComponentType } from "react";
import { createRoot } from "react-dom/client";
import { I18nProvider, useI18n } from "./i18n";
import { ForgotPasswordPage, LoginPage, ResetPasswordPage, SignupPage, VerifyEmailPage } from "./pages/auth";
import { setShowBlocks } from "./calendar/data";
import { setTimeFormat } from "./calendar/format";
import { validateCalendarSearch } from "./calendar/page";
import { HomePage } from "./pages/shell";
import { useMe, useSetupStatus } from "./session";
import { Alert, AuthCard } from "./ui";

// Pages rarement ouvertes : chargées à la demande.
const SettingsPage = lazy(() => import("./pages/settings").then((m) => ({ default: m.SettingsPage })));
const SetupPage = lazy(() => import("./pages/setup").then((m) => ({ default: m.SetupPage })));
// Pages publiques de réservation : accessibles sans compte.
const BookingHomePage = lazy(() => import("./pages/public-booking").then((m) => ({ default: m.BookingHomePage })));
const BookingTypePage = lazy(() => import("./pages/public-booking").then((m) => ({ default: m.BookingTypePage })));
const PrivacyPage = lazy(() => import("./pages/legal").then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import("./pages/legal").then((m) => ({ default: m.TermsPage })));
const BookingManagePage = lazy(() => import("./pages/public-booking").then((m) => ({ default: m.BookingManagePage })));

function Root() {
  const { t, setLocale } = useI18n();
  const setup = useSetupStatus();
  const me = useMe();
  const path = useRouterState({ select: (s) => s.location.pathname });

  // La langue enregistrée sur le compte l'emporte sur celle du navigateur.
  useEffect(() => {
    if (me.data) setLocale(me.data.locale);
  }, [me.data?.locale]);
  // Préférences d'affichage, appliquées avant le rendu des vues.
  setTimeFormat(me.data?.prefs?.timeFormat);
  setShowBlocks(me.data?.prefs?.showBlocks);
  const theme = me.data?.prefs?.theme;
  useEffect(() => {
    // « Système » : aucun attribut, la page suit le réglage clair ou sombre de l'appareil.
    if (theme === "light" || theme === "dark") document.documentElement.dataset.theme = theme;
    else delete document.documentElement.dataset.theme;
  }, [theme]);

  if (setup.isPending || me.isPending) return <div className="boot" aria-busy="true" />;
  if (setup.isError || me.isError) {
    return (
      <AuthCard title="Maki Calendar">
        <Alert>{t("error.NETWORK")}</Alert>
      </AuthCard>
    );
  }
  if (!setup.data.completed && path !== "/setup") {
    return (
      <AuthCard title={t("setup.title")}>
        <Alert kind="info">{t("setup.noToken")}</Alert>
      </AuthCard>
    );
  }
  return (
    <Suspense fallback={<div className="boot" aria-busy="true" />}>
      <Outlet />
    </Suspense>
  );
}

/** Pages réservées aux personnes connectées. */
const authed = (Page: ComponentType) =>
  function Guarded() {
    const me = useMe();
    if (!me.data) return <Navigate to="/login" />;
    return <Page />;
  };

/** Pages de connexion : inutile d'y rester une fois connecté. */
const guest = (Page: ComponentType) =>
  function GuestOnly() {
    const me = useMe();
    if (me.data) return <Navigate to="/" />;
    return <Page />;
  };

function SetupGate() {
  const setup = useSetupStatus();
  if (setup.data?.completed) return <Navigate to="/" />;
  return <SetupPage />;
}

const rootRoute = createRootRoute({ component: Root });
const routes = [
  createRoute({ getParentRoute: () => rootRoute, path: "/", component: authed(HomePage), validateSearch: validateCalendarSearch }),
  createRoute({ getParentRoute: () => rootRoute, path: "/settings", component: authed(SettingsPage) }),
  createRoute({ getParentRoute: () => rootRoute, path: "/login", component: guest(LoginPage) }),
  createRoute({ getParentRoute: () => rootRoute, path: "/signup", component: guest(SignupPage) }),
  createRoute({ getParentRoute: () => rootRoute, path: "/forgot-password", component: guest(ForgotPasswordPage) }),
  createRoute({ getParentRoute: () => rootRoute, path: "/verify-email", component: VerifyEmailPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/reset-password", component: ResetPasswordPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/setup", component: SetupGate }),
  createRoute({ getParentRoute: () => rootRoute, path: "/b/manage/$token", component: BookingManagePage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/privacy", component: PrivacyPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/terms", component: TermsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/b/$slug", component: BookingHomePage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/b/$slug/$type", component: BookingTypePage }),
];
const router = createRouter({
  routeTree: rootRoute.addChildren(routes),
  defaultNotFoundComponent: () => <Navigate to="/" />,
});

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <RouterProvider router={router} />
      </I18nProvider>
    </QueryClientProvider>
  </StrictMode>,
);
